/**
 * Overlay tokens — extends the shared chrome vocabulary with the
 * spectator-overlay-specific layout tokens (panel widths, accolade
 * window, etc.).
 *
 * Shared shape / spacing / typography / color tokens live in
 * `@pilot-protocol/theme/chrome`. This module re-exports them under
 * the `OverlayTokens` umbrella so the existing `<MatchOverlay tokens={…}>`
 * prop and `tokensToCssVars(...)` helper API keeps working for
 * consumers (storybook bench, marketing app) without churn.
 */

import {
  type ChromeTokens,
  DEFAULT_CHROME_TOKENS,
  chromeTokensToCssVars,
  notchedClipPath as _notchedClipPath,
  parsePx as _parsePx,
} from "@pilot-protocol/theme/chrome";

export interface OverlayLayoutTokens {
  // ── Layout (overlay-specific) ─────────────────────────────────────
  panelWidth: string;
  /** Top metadata strip height. */
  stripHeight: string;
  /** Height reserved for the cosmetic slot inside a SideStatPanel
   *  (agent portrait / faction emblem / chassis thumbnail). Consumers
   *  fill it via the `cosmeticSlot` prop. Set "0" to suppress. */
  cosmeticSlotHeight: string;
  /** Width of the bottom-anchored ReasoningPanel (O2). Wider than the
   *  side stat panel since reasoning_text needs reading space. */
  reasoningPanelWidth: string;
  /** Max height of the ReasoningPanel before its body scrolls. */
  reasoningMaxHeight: string;
  /** Width of the bottom-center TelemetryFeed (O3). */
  telemetryWidth: string;
  /** Default number of telemetry rows visible at once. */
  telemetryMaxRows: number;
  /** Width of the top-center AccoladeFlash (O5). */
  accoladeWidth: string;
  /** Ticks-since-trigger that an accolade stays visible before fading.
   *  At default 4ms-per-tick scrubbing speed, ~2 ticks is ~1.5s on
   *  screen — enough to read, not so long it overstays. */
  accoladeWindowTicks: number;
}

export type OverlayTokens = ChromeTokens & OverlayLayoutTokens;

const DEFAULT_OVERLAY_LAYOUT: OverlayLayoutTokens = {
  panelWidth: "248px",
  stripHeight: "40px",
  cosmeticSlotHeight: "92px",
  reasoningPanelWidth: "360px",
  reasoningMaxHeight: "240px",
  telemetryWidth: "420px",
  telemetryMaxRows: 8,
  accoladeWidth: "320px",
  accoladeWindowTicks: 2,
};

export const DEFAULT_TOKENS: OverlayTokens = {
  ...DEFAULT_CHROME_TOKENS,
  ...DEFAULT_OVERLAY_LAYOUT,
};

/** Map a partial token override onto the full set. */
export function resolveTokens(overrides?: Partial<OverlayTokens>): OverlayTokens {
  return overrides ? { ...DEFAULT_TOKENS, ...overrides } : DEFAULT_TOKENS;
}

/**
 * Translate the token record into a `style` object that sets every
 * `--pp-*` CSS variable (chrome + overlay layout). Apply on the overlay
 * root so descendants pick them up via `var()` references.
 */
export function tokensToCssVars(t: OverlayTokens): React.CSSProperties {
  return {
    ...chromeTokensToCssVars(t),
    "--pp-panel-width": t.panelWidth,
    "--pp-strip-height": t.stripHeight,
    "--pp-cosmetic-slot-h": t.cosmeticSlotHeight,
    "--pp-reasoning-panel-w": t.reasoningPanelWidth,
    "--pp-reasoning-max-h": t.reasoningMaxHeight,
    "--pp-telemetry-w": t.telemetryWidth,
    "--pp-accolade-w": t.accoladeWidth,
  } as React.CSSProperties;
}

// Re-export shape helpers so existing overlay code paths still work.
export const notchedClipPath = _notchedClipPath;
export const parsePx = _parsePx;
